// BracketTemplates.js - Template predefiniti per tabelloni di eliminazione diretta
// Supporta da 2 a 16 squadre con gestione automatica dei bye

/**
 * Genera un ID univoco per match e connessioni
 */
let idCounter = 0;
const generateId = (prefix) => `${prefix}-${++idCounter}`;
const resetIdCounter = () => { idCounter = 0; };

/**
 * Genera un tabellone singola eliminazione per N squadre (2-16)
 * Gestisce automaticamente i bye per numeri non potenze di 2
 *
 * Logica bye: le squadre con seed più alto (1, 2, etc.) saltano il primo turno
 * Esempio 6 squadre:
 * - Squadre 1,2 hanno bye -> entrano in semifinale
 * - Squadre 3v4 e 5v6 giocano i quarti
 * - Vincenti quarti vs squadre 1,2 in semifinale
 */
const createBracketForTeams = (numTeams) => {
  resetIdCounter();

  if (numTeams < 2 || numTeams > 16) {
    return createEmptyTemplate();
  }

  // Caso speciale: 2 squadre = solo finale
  if (numTeams === 2) {
    const finale = generateId('match');
    return {
      name: 'Finale (2 squadre)',
      teamsRequired: 2,
      matches: [{ id: finale, name: 'Finale', x: 50, y: 50 }],
      connections: [],
      inputMapping: [
        { externalIndex: 0, matchId: finale, portId: 'in-1' },
        { externalIndex: 1, matchId: finale, portId: 'in-2' }
      ],
      outputMapping: [
        { externalIndex: 0, matchId: finale, portId: 'winner', label: '1° posto' },
        { externalIndex: 1, matchId: finale, portId: 'loser', label: '2° posto' }
      ]
    };
  }

  const bracketSize = Math.pow(2, Math.ceil(Math.log2(numTeams)));
  const numByes = bracketSize - numTeams;
  const firstRoundMatches = (numTeams - numByes) / 2;
  const hasPrelims = numByes > 0 && firstRoundMatches > 0;

  const MATCH_HEIGHT = 70;
  const HORIZONTAL_GAP = 160;

  const matches = [];
  const connections = [];
  const inputMapping = [];
  const rounds = [];

  let currentX = 20;

  // === TURNO PRELIMINARE (solo se non è potenza di 2) ===
  if (hasPrelims) {
    const prelimRound = [];

    for (let i = 0; i < firstRoundMatches; i++) {
      const matchId = generateId('match');
      const matchY = 20 + i * (MATCH_HEIGHT + 20);

      matches.push({
        id: matchId,
        name: firstRoundMatches <= 2 ? `Quarto ${i + 1}` : `Preliminare ${i + 1}`,
        x: currentX,
        y: matchY
      });
      prelimRound.push(matchId);

      // Squadre che giocano: seed (numByes + 2*i + 1) vs (numByes + 2*i + 2)
      const seed1 = numByes + (i * 2) + 1;
      const seed2 = numByes + (i * 2) + 2;

      inputMapping.push({ externalIndex: seed1 - 1, matchId, portId: 'in-1' });
      inputMapping.push({ externalIndex: seed2 - 1, matchId, portId: 'in-2' });
    }

    rounds.push(prelimRound);
    currentX += HORIZONTAL_GAP;
  }

  // === TURNI PRINCIPALI ===
  // Dopo preliminari: bracketSize/4 match (es: 6 sq -> 2 semifinali)
  // Senza preliminari: bracketSize/2 match (potenza di 2)
  let matchesThisRound = hasPrelims ? bracketSize / 4 : bracketSize / 2;

  while (matchesThisRound >= 1) {
    const currentRound = [];
    const isFirstMainRound = rounds.length === (hasPrelims ? 1 : 0);

    // Nome del turno
    let roundName;
    if (matchesThisRound === 1) roundName = 'Finale';
    else if (matchesThisRound === 2) roundName = 'Semifinale';
    else if (matchesThisRound === 4) roundName = 'Quarto';
    else roundName = 'Match';

    for (let i = 0; i < matchesThisRound; i++) {
      const matchId = generateId('match');
      // Calcola Y per centrare verticalmente
      const spacing = (MATCH_HEIGHT + 20) * (hasPrelims ? firstRoundMatches : bracketSize / 2) / matchesThisRound;
      const matchY = 20 + i * spacing + (spacing - MATCH_HEIGHT) / 2;

      matches.push({
        id: matchId,
        name: matchesThisRound > 1 ? `${roundName} ${i + 1}` : roundName,
        x: currentX,
        y: matchY
      });
      currentRound.push(matchId);

      // === DETERMINA INPUT ===
      if (isFirstMainRound && hasPrelims) {
        // Primo turno principale dopo preliminari
        // Alterna bye e vincitori: ogni match riceve 1 bye + 1 vincitore (se disponibili)
        const prelimRound = rounds[0];

        // Crea lista input alternando bye e vincitori
        const allInputs = [];
        let byeIdx = 0;
        let winnerIdx = 0;
        const totalSlots = matchesThisRound * 2;

        for (let slot = 0; slot < totalSlots; slot++) {
          // Alterna: slot pari = bye (se disponibile), slot dispari = vincitore (se disponibile)
          if (slot % 2 === 0 && byeIdx < numByes) {
            allInputs.push({ type: 'bye', teamIndex: byeIdx++ });
          } else if (winnerIdx < prelimRound.length) {
            allInputs.push({ type: 'winner', matchIndex: winnerIdx++ });
          } else if (byeIdx < numByes) {
            allInputs.push({ type: 'bye', teamIndex: byeIdx++ });
          }
        }

        // Assegna input a questo match
        const input1 = allInputs[i * 2];
        const input2 = allInputs[i * 2 + 1];

        // in-1
        if (input1?.type === 'bye') {
          inputMapping.push({ externalIndex: input1.teamIndex, matchId, portId: 'in-1' });
        } else if (input1?.type === 'winner') {
          connections.push({
            id: generateId('conn'),
            fromMatchId: prelimRound[input1.matchIndex],
            fromPort: 'winner',
            toMatchId: matchId,
            toPort: 'in-1'
          });
        }

        // in-2
        if (input2?.type === 'bye') {
          inputMapping.push({ externalIndex: input2.teamIndex, matchId, portId: 'in-2' });
        } else if (input2?.type === 'winner') {
          connections.push({
            id: generateId('conn'),
            fromMatchId: prelimRound[input2.matchIndex],
            fromPort: 'winner',
            toMatchId: matchId,
            toPort: 'in-2'
          });
        }
      } else if (isFirstMainRound && !hasPrelims) {
        // Primo turno, potenza di 2: input diretti
        inputMapping.push({ externalIndex: i * 2, matchId, portId: 'in-1' });
        inputMapping.push({ externalIndex: i * 2 + 1, matchId, portId: 'in-2' });
      } else {
        // Turni successivi: vincitori del turno precedente
        const prevRound = rounds[rounds.length - 1];

        if (i * 2 < prevRound.length) {
          connections.push({
            id: generateId('conn'),
            fromMatchId: prevRound[i * 2],
            fromPort: 'winner',
            toMatchId: matchId,
            toPort: 'in-1'
          });
        }
        if (i * 2 + 1 < prevRound.length) {
          connections.push({
            id: generateId('conn'),
            fromMatchId: prevRound[i * 2 + 1],
            fromPort: 'winner',
            toMatchId: matchId,
            toPort: 'in-2'
          });
        }
      }
    }

    rounds.push(currentRound);
    currentX += HORIZONTAL_GAP;
    matchesThisRound = matchesThisRound / 2;
  }

  // Ordina inputMapping per externalIndex
  inputMapping.sort((a, b) => a.externalIndex - b.externalIndex);

  // Output mapping - finale
  const finalMatchId = rounds[rounds.length - 1][0];
  const outputMapping = [
    { externalIndex: 0, matchId: finalMatchId, portId: 'winner', label: '1° posto' },
    { externalIndex: 1, matchId: finalMatchId, portId: 'loser', label: '2° posto' }
  ];

  return {
    name: `Eliminazione diretta (${numTeams} squadre)`,
    teamsRequired: numTeams,
    matches,
    connections,
    inputMapping,
    outputMapping
  };
};

/**
 * Template vuoto per costruzione manuale
 */
const createEmptyTemplate = () => {
  return {
    name: 'Tabellone vuoto',
    teamsRequired: 0,
    matches: [],
    connections: [],
    inputMapping: [],
    outputMapping: []
  };
};

/**
 * Tutti i template disponibili
 */
export const BRACKET_TEMPLATES = {
  'empty': createEmptyTemplate,
  '2-teams': () => createBracketForTeams(2),
  '3-teams': () => createBracketForTeams(3),
  '4-teams': () => createBracketForTeams(4),
  '5-teams': () => createBracketForTeams(5),
  '6-teams': () => createBracketForTeams(6),
  '7-teams': () => createBracketForTeams(7),
  '8-teams': () => createBracketForTeams(8),
  '9-teams': () => createBracketForTeams(9),
  '10-teams': () => createBracketForTeams(10),
  '11-teams': () => createBracketForTeams(11),
  '12-teams': () => createBracketForTeams(12),
  '13-teams': () => createBracketForTeams(13),
  '14-teams': () => createBracketForTeams(14),
  '15-teams': () => createBracketForTeams(15),
  '16-teams': () => createBracketForTeams(16)
};

/**
 * Lista dei template per UI dropdown
 */
export const TEMPLATE_OPTIONS = [
  { value: 'empty', label: 'Vuoto', teams: 0 },
  { value: '2-teams', label: '2 squadre', teams: 2 },
  { value: '3-teams', label: '3 squadre', teams: 3 },
  { value: '4-teams', label: '4 squadre', teams: 4 },
  { value: '5-teams', label: '5 squadre', teams: 5 },
  { value: '6-teams', label: '6 squadre', teams: 6 },
  { value: '7-teams', label: '7 squadre', teams: 7 },
  { value: '8-teams', label: '8 squadre', teams: 8 },
  { value: '9-teams', label: '9 squadre', teams: 9 },
  { value: '10-teams', label: '10 squadre', teams: 10 },
  { value: '11-teams', label: '11 squadre', teams: 11 },
  { value: '12-teams', label: '12 squadre', teams: 12 },
  { value: '13-teams', label: '13 squadre', teams: 13 },
  { value: '14-teams', label: '14 squadre', teams: 14 },
  { value: '15-teams', label: '15 squadre', teams: 15 },
  { value: '16-teams', label: '16 squadre', teams: 16 }
];

/**
 * Applica un template a un nodo compound esistente
 * @param {string} templateKey - Chiave del template
 * @returns {object} Struttura del template
 */
export const applyTemplate = (templateKey) => {
  const templateFn = BRACKET_TEMPLATES[templateKey];
  if (!templateFn) {
    console.warn(`Template "${templateKey}" non trovato, uso template vuoto`);
    return createEmptyTemplate();
  }
  return templateFn();
};

/**
 * Calcola le dimensioni necessarie per un tabellone
 * @param {Array} matches - Lista dei match
 * @returns {object} { width, height }
 */
export const calculateBracketDimensions = (matches) => {
  if (!matches || matches.length === 0) {
    return { width: 200, height: 100 };
  }

  const MATCH_WIDTH = 120;
  const MATCH_HEIGHT = 60;
  const PADDING = 40;

  let maxX = 0;
  let maxY = 0;

  matches.forEach(match => {
    const right = match.x + MATCH_WIDTH;
    const bottom = match.y + MATCH_HEIGHT;
    if (right > maxX) maxX = right;
    if (bottom > maxY) maxY = bottom;
  });

  return {
    width: maxX + PADDING,
    height: maxY + PADDING
  };
};

export default BRACKET_TEMPLATES;
